import { useEffect, useRef, useState } from "react";
import { ICONES, Icone, hora } from "../visual.jsx";
import { haQuantoTempo } from "../caso.js";
import { useNavegacao } from "../navegacao.jsx";
import { useAcoes, useEscopoEmPalavras, useEstado } from "../estado.jsx";
import { Botao, Pontinhos, Rolagem, SeloIA } from "./comuns.jsx";

/**
 * O CATÁLOGO dos elos do ciclo — e ele é contrato com o servidor.
 *
 * `PASSOS` em `src/copiloto/delegacao.ts` publica a chave; aqui mora a frase que a pessoa
 * lê. Passo que o servidor manda e a tela não sabe nomear apareceria como uma
 * barra girando sem texto, que é o "trabalhando…" genérico que este palco existe
 * para apagar — `palco.test.jsx` reprova a falta.
 */
export const PASSOS = {
  assumiu: { rotulo: "Assumiu o atendimento", icone: "ia" },
  insistindo: { rotulo: "Voltou a falar com o cliente calado", icone: "relogio" },
  lendo: { rotulo: "Lendo o caso", icone: "busca" },
  escrevendo: { rotulo: "Escrevendo a mensagem", icone: "editar" },
  conferindo: { rotulo: "Conferindo os limites", icone: "cadeado" },
  fila: { rotulo: "Esperando aprovação em Automação", icone: "ampulheta" },
  enviando: { rotulo: "Enviando ao cliente", icone: "enviar" },
  etapa: { rotulo: "Avaliando a etapa do funil", icone: "matriz" },
  /* O fim do ciclo. Chegam como `acao` do evento, não como `passo` — para o
     palco parar de girar e dizer o que ficou. */
  respondeu: { rotulo: "Respondeu o cliente", icone: "certo" },
  moveu: { rotulo: "Moveu o caso de etapa", icone: "avancar" },
  sugeriu: { rotulo: "Escreveu a sugestão na trilha", icone: "nota" },
  devolvido: { rotulo: "Devolveu o atendimento para uma pessoa", icone: "pessoa" },
  /* A IA manda o contrato sozinha desde 09/09 (decisão do escritório): o link de
     assinatura sai para o cliente sem fila e sem interruptor, então o palco tem
     de DIZER isso — pacote saindo em silêncio é a coisa mais cara de descobrir
     depois. */
  contrato: { rotulo: "Enviou o contrato para o cliente assinar", icone: "documento" },
};

/**
 * A ESPINHA do ciclo: os cinco elos por onde toda resposta passa, na ordem em
 * que `conduzir` (src/copiloto/delegacao.ts) os percorre. É ela que responde "o que já
 * fez, o que faz agora, o que vem depois" sem ninguém precisar perguntar.
 */
const ESPINHA = [
  { chave: "lendo", rotulo: "Lê o caso", nota: "conversa, ficha, documentos e prazos" },
  { chave: "escrevendo", rotulo: "Escreve a mensagem", nota: "no tom do agente e no objetivo da etapa" },
  { chave: "conferindo", rotulo: "Confere os limites", nota: "valores, protocolo, pedido de gente" },
  { chave: "envio", rotulo: "Manda a mensagem", nota: "direto ao cliente ou para aprovação" },
  { chave: "etapa", rotulo: "Avalia a etapa", nota: "só avança com o critério do escritório cumprido" },
];

/* Onde cada passo do servidor cai na espinha. `fila` e `enviando` são o MESMO
   elo com dois destinos — dois elos fariam a barra andar por causa do ajuste de
   automação, e não por causa do trabalho. */
const NA_ESPINHA = { assumiu: 0, insistindo: 0, lendo: 0, escrevendo: 1, conferindo: 2, fila: 3, enviando: 3, etapa: 4 };
const TERMINAIS = new Set(["respondeu", "moveu", "sugeriu", "devolvido"]);

/* Depois disto o ciclo não está mais em curso: ou terminou sem evento de fim
   (motor caiu, processo morreu) ou a IA está de plantão. Dizer "escrevendo" meia
   hora depois seria a tela mentindo, que é pior que não dizer nada. */
const CICLO_VELHO = 4 * 60_000;

/**
 * O relógio do palco: o "há 3min" anda, e o ciclo abandonado envelhece sozinho.
 *
 * O agora vem daqui e não de `Date.now()` no corpo do render — relógio lido na
 * pintura é função impura, e é a mesma regra que a faixa da barra de escrever já
 * segue. Zero na primeira pintura é "ainda não sei que horas são", e nesse
 * quadro o ciclo conta como fresco: o efeito acerta no quadro seguinte.
 */
function useAgora() {
  const [agora, setAgora] = useState(0);
  useEffect(() => {
    setAgora(Date.now());
    const t = setInterval(() => setAgora(Date.now()), 5000);
    return () => clearInterval(t);
  }, []);
  return agora;
}

/** Um elo da espinha, com o estado dele escrito e desenhado. */
function Elo({ passo, estado, detalhe }) {
  const cor = estado === "feito" ? "text-verde-fundo" : estado === "agora" ? "text-foreground" : "text-muted-foreground";
  return (
    <li className="flex gap-3">
      <div className="flex flex-col items-center">
        <span
          className={`flex size-[26px] flex-none items-center justify-center rounded-full border ${
            estado === "feito"
              ? "border-verde/40 bg-verde/15 text-verde-fundo"
              : estado === "agora"
                ? "border-verde bg-verde text-creme animate-[vRespira_1.8s_ease-in-out_infinite]"
                : "border-border bg-card text-muted-foreground"
          }`}
        >
          <Icone de={ICONES[estado === "feito" ? "certo" : (PASSOS[passo.chave]?.icone ?? "ia")]} tamanho={14} />
        </span>
        {/* O fio que liga os elos: acende no que já passou. */}
        <span className={`mt-1 w-px flex-1 ${estado === "feito" ? "bg-verde/40" : "bg-border"}`} />
      </div>
      <div className="min-w-0 flex-1 pb-4">
        <div className={`flex items-center gap-2 text-corpo font-bold ${cor}`}>
          {passo.rotulo}
          {estado === "agora" ? <Pontinhos cor="bg-verde" /> : null}
        </div>
        <div className="mt-0.5 text-apoio leading-[1.45] text-muted-foreground">
          {estado === "agora" && detalhe ? detalhe : passo.nota}
        </div>
      </div>
    </li>
  );
}

/** A mensagem nascendo, letra a letra — o mesmo desenho da peça sendo montada. */
function Compondo({ texto, fechado }) {
  const caixa = useRef(null);
  useEffect(() => {
    const el = caixa.current;
    if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 60) el.scrollTop = el.scrollHeight;
  }, [texto]);

  return (
    <div className="flex min-h-0 flex-1 flex-col rounded-[14px] border border-verde/25 bg-card p-[14px_16px]">
      <div className="mb-2 flex items-center gap-2">
        <span className="text-selo font-bold tracking-[.06em] text-verde-fundo">
          {fechado ? "A MENSAGEM QUE ELA ESCREVEU" : "ESCREVENDO AGORA"}
        </span>
        <span className="ml-auto font-mono text-selo text-muted-foreground">{(texto ?? "").length} caracteres</span>
      </div>
      <div ref={caixa} className="min-h-[90px] overflow-y-auto text-corpo leading-[1.6] whitespace-pre-wrap text-tinta-media">
        {texto}
        {fechado ? null : (
          <span className="ml-0.5 inline-block h-3.5 w-0.5 animate-[vCaret_.9s_infinite] bg-verde align-middle" />
        )}
      </div>
    </div>
  );
}

/**
 * O que a IA JÁ FEZ neste atendimento — da trilha, que é o que sobrevive ao F5.
 *
 * O ciclo em curso vive no evento; o histórico vive no banco. Misturar os dois
 * na mesma lista duplicaria a resposta que acabou de sair (ela é evento agora e
 * trilha daqui a um segundo).
 */
function JaFez({ caso, delegacao }) {
  const desde = delegacao?.criado_em ?? "";
  const feitos = [
    ...(caso?.trilha ?? [])
      .filter((e) => (e.autor === "copiloto" || String(e.autor ?? "").startsWith("fluxo:")) && (e.criado_em ?? "") >= desde)
      .map((e) => ({ id: `t${e.id}`, quando: e.criado_em, texto: e.texto ?? `${e.de ?? ""} → ${e.para ?? ""}` })),
    ...(caso?.mensagens ?? [])
      .filter((m) => m.direcao === "saida" && m.autor === "copiloto" && (m.quando ?? "") >= desde)
      .map((m) => ({ id: `m${m.id}`, quando: m.quando, texto: `Mandou: ${m.texto ?? ""}` })),
  ]
    .toSorted((a, b) => String(b.quando).localeCompare(String(a.quando)))
    .slice(0, 20);

  if (!feitos.length)
    return <div className="text-apoio leading-[1.5] text-muted-foreground">Nada ainda — ela assumiu agora há pouco.</div>;

  return (
    <ol className="flex flex-col gap-2.5">
      {feitos.map((f) => (
        <li key={f.id} className="flex gap-2.5">
          <span className="mt-[3px] flex-none font-mono text-selo text-muted-foreground">{hora(f.quando)}</span>
          <span className="min-w-0 flex-1 text-apoio leading-[1.5] text-tinta-media">{String(f.texto).slice(0, 220)}</span>
        </li>
      ))}
    </ol>
  );
}

/** O que vai acontecer — a regra, não um palpite: escopo, limites e os freios. */
function ASeguir({ delegacao, ultima, aguardando }) {
  const { ajustes } = useEstado();
  const emPalavras = useEscopoEmPalavras();
  const horas = Number(ajustes?.ia_horas_insistir) || 20;
  const teto = Number(ajustes?.ia_teto_insistencia) || 3;
  const limites = (() => {
    try {
      return JSON.parse(delegacao?.limites || "{}");
    } catch {
      return {};
    }
  })();

  const linhas = [
    { icone: "matriz", texto: emPalavras(delegacao) },
    aguardando
      ? {
          icone: "relogio",
          texto: `Se o cliente não responder em ${horas}h, ela fala de novo — no máximo ${teto} vezes, e depois devolve o caso para uma pessoa.`,
        }
      : { icone: "enviar", texto: "Assim que a mensagem sair, ela espera a resposta do cliente." },
    limites.sem_valores !== false ? { icone: "cadeado", texto: "Não fala de valor, honorário nem percentual." } : null,
    limites.sem_protocolo !== false ? { icone: "cadeado", texto: "Não protocola e não assina nada." } : null,
    limites.escalar !== false ? { icone: "pessoa", texto: "Se o cliente pedir uma pessoa, ela devolve na hora." } : null,
    ultima?.direcao === "saida"
      ? { icone: "whatsapp", texto: `A bola está com o cliente ${haQuantoTempo(ultima.quando)}.` }
      : null,
  ].filter(Boolean);

  return (
    <ul className="flex flex-col gap-2.5">
      {linhas.map((l, i) => (
        <li key={i} className="flex gap-2.5">
          <span className="mt-[2px] flex-none text-muted-foreground">
            <Icone de={ICONES[l.icone]} tamanho={15} />
          </span>
          <span className="min-w-0 flex-1 text-apoio leading-[1.5] text-tinta-media">{l.texto}</span>
        </li>
      ))}
    </ul>
  );
}

function Coluna({ titulo, children }) {
  return (
    <section className="flex min-h-0 flex-col rounded-[14px] border border-border bg-card p-[13px_15px]">
      <div className="mb-2.5 text-selo font-bold tracking-[.06em] text-muted-foreground">{titulo}</div>
      <div className="min-h-0 flex-1 overflow-hidden">
        <Rolagem>{children}</Rolagem>
      </div>
    </section>
  );
}

/**
 * O PALCO: a IA conduzindo o atendimento, ocupando a conversa inteira.
 *
 * A delegação já dizia "IA no comando" numa faixa de 40px dentro da barra de
 * escrever, e era tudo: entre assumir e a mensagem sair passa perto de um minuto
 * de modelo, e nesse minuto quem delegou olhava uma conversa parada. Aqui o
 * cabeçalho, a conversa e o compositor viram UMA superfície que responde as três
 * perguntas ao mesmo tempo — o que ela já fez, o que está fazendo agora (com o
 * texto nascendo letra a letra) e o que vem depois.
 *
 * Não é modal e não trava ninguém: "Ver a conversa" (`?palco=0`) devolve a mesa
 * de sempre, com a faixa da barra de escrever no lugar dela.
 */
export default function PalcoDaIA({ caso }) {
  const { iaEmAcao, envio } = useEstado();
  const { retomar } = useAcoes();
  const { ajustar, casoId } = useNavegacao();
  const agora = useAgora();

  const delegacao = caso?.delegacao;
  if (!delegacao) return null;

  /* Só o ciclo DESTE caso, e só enquanto ele é recente: evento de outra conversa
     ou de meia hora atrás faria o palco afirmar trabalho que não existe. */
  const emAcao = iaEmAcao?.id === casoId && (!agora || agora - (iaEmAcao?.quando ?? 0) < CICLO_VELHO) ? iaEmAcao : null;
  const passo = emAcao?.passo ?? null;
  const terminou = TERMINAIS.has(passo);
  const atual = terminou ? ESPINHA.length : (NA_ESPINHA[passo] ?? -1);
  const ultima = (caso?.mensagens ?? []).at(-1);
  const ultimaDela = (caso?.mensagens ?? []).findLast((m) => m.direcao === "saida" && m.autor === "copiloto")?.texto ?? "";
  const aguardando = !passo || terminou;

  const nome = delegacao.agente_nome ?? "Assistente do escritório";
  const cabeca = terminou
    ? PASSOS[passo].rotulo
    : passo
      ? (PASSOS[passo]?.rotulo ?? "Trabalhando no caso")
      : ultima?.direcao === "entrada"
        ? "Vai responder a mensagem do cliente"
        : "De plantão — a bola está com o cliente";

  return (
    <div className="absolute inset-0 z-[14] flex animate-[vFade_var(--t-curta)_var(--e-entra)_both] flex-col bg-mesa">
      {/* O cabeçalho: quem está no comando, fazendo o quê, e as saídas. */}
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-b-border bg-card p-[13px_16px]">
        <span className="flex flex-none items-center gap-2.5">
          <span className="relative flex size-[38px] items-center justify-center rounded-[11px] bg-verde/15 text-[19px]">
            {delegacao.agente_emoji ?? <SeloIA tamanho={20} fonte={10} />}
            {!aguardando ? (
              <span className="absolute -right-0.5 -bottom-0.5 size-[11px] rounded-full border-2 border-card bg-verde animate-[vRespira_1.8s_ease-in-out_infinite]" />
            ) : null}
          </span>
          <span className="flex flex-col">
            <span className="flex items-center gap-2">
              <span className="text-forte font-bold">{nome}</span>
              <span className="rounded-full bg-verde/15 px-2 py-[1px] text-selo font-bold tracking-[.06em] text-verde-fundo">
                IA NO COMANDO
              </span>
            </span>
            <span className="text-apoio text-tinta-suave">
              {cabeca} · {envio} · turno {delegacao.turnos + (aguardando ? 0 : 1)}
            </span>
          </span>
        </span>
        <span className="ml-auto flex flex-wrap items-center gap-2">
          <Botao
            rotulo="Ver a conversa"
            aoTocar={() => ajustar({ palco: "0" })}
            titulo="Volta para a mesa; a IA continua conduzindo"
          />
          <Botao rotulo="Ajustar" aoTocar={() => ajustar({ delegar: true })} titulo="Até onde a IA vai sozinha neste caso" />
          <Botao destaque rotulo="Retomar" aoTocar={retomar} titulo="O atendimento volta para você agora" />
        </span>
      </header>

      <div className="grid min-h-0 flex-1 gap-3 overflow-y-auto p-3 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,.85fr)] lg:overflow-hidden">
        {/* O CICLO — o que ela faz agora, com o texto nascendo embaixo. */}
        <div className="flex min-h-0 flex-col gap-3">
          <section className="rounded-[14px] border border-border bg-card p-[15px_16px]">
            <div className="mb-3 flex flex-col gap-0.5">
              <span className="text-selo font-bold tracking-[.06em] text-muted-foreground">O CICLO DESTA RESPOSTA</span>
              {/* Cinco elos cinzas sem uma palavra parecem tela quebrada — em
                  repouso o cartão diz que está em repouso, e o que acorda ele. */}
              {aguardando ? (
                <span className="text-apoio text-muted-foreground">
                  em repouso · começa quando o cliente falar{ultima?.direcao === "saida" ? " ou o silêncio estourar" : ""}
                </span>
              ) : null}
            </div>
            <ol className="flex flex-col [&>li:last-child>div:first-child>span:last-child]:hidden">
              {ESPINHA.map((p, i) => (
                <Elo
                  key={p.chave}
                  passo={p}
                  detalhe={emAcao?.detalhe}
                  estado={i < atual ? "feito" : i === atual ? "agora" : "futuro"}
                />
              ))}
            </ol>
          </section>
          {/* Sem ciclo em curso o espaço mostra a ÚLTIMA mensagem que ela
              escreveu: metade da tela em branco não diz nada, e é justamente o
              texto que quem chega quer conferir. */}
          {emAcao?.parcial ? (
            <Compondo texto={emAcao.parcial} fechado={terminou} />
          ) : ultimaDela ? (
            <Compondo texto={ultimaDela} fechado />
          ) : null}
        </div>

        {/* O passado e o futuro, um em cima do outro. */}
        <div className="grid min-h-0 grid-rows-2 gap-3">
          <Coluna titulo="JÁ FEZ NESTE ATENDIMENTO">
            <JaFez caso={caso} delegacao={delegacao} />
          </Coluna>
          <Coluna titulo="O QUE VEM DEPOIS">
            <ASeguir delegacao={delegacao} ultima={ultima} aguardando={aguardando} />
          </Coluna>
        </div>
      </div>
    </div>
  );
}
